// React and external dependencies

import { materialTypeFormat } from '@/common/material-type-formatting';
import { useGetUploadUrlMutation } from '@/common/utils/graphql.generated';
import { useFacility } from '@/contexts/facility-context/hook';
import { useCurrentUser } from '@/hooks';
import { useSet } from '@mantine/hooks';
import React, { createContext, ReactNode, useCallback, useMemo, useState } from 'react';
import { v4 as randomUUID } from 'uuid';
import {
	MaterialType,
	MediaType,
	ScanEventPhotoType,
	ScanEventType,
} from '../../../../../../backend/src/types.generated';
import { useSearchTrackableAssetsForFacilitiesBaleOutboundLazyQuery } from '../bale-outbound/graphql.generated';
import {
	useCreateInboundLoadMutation,
	useSearchForLoadLazyQuery,
	useUploadInboundBalePhotoMutation,
} from './graphql.generated';

export type BaleInboundPageType =
	| 'RECEIVING'
	| 'INITIAL_SCAN'
	| 'SCAN_ADD'
	| 'DOCKET_EDIT'
	| 'EVALUATION'
	| 'CONFIRMATION'
	| 'DOCKET_PHOTO'
	| 'MANUAL_CREATE:ADD_BALE'
	| 'MANUAL_CREATE:REMOVE_BALE'
	| 'MANUAL_CREATE:ROOT'
	| 'ADD_LOAD_PHOTOS';

export interface BaleInboundContextValue {
	// Navigation state
	page: BaleInboundPageType;
	setPage: React.Dispatch<React.SetStateAction<BaleInboundPageType>>;
	manualCodeEntryOpen: boolean;
	setManualCodeEntryOpen: React.Dispatch<React.SetStateAction<boolean>>;
	handleScan: (code: string) => Promise<{
		result: string;
	}>;
	workflowType: 'MANUAL' | 'DEFAULT';
	setWorkflowType: React.Dispatch<React.SetStateAction<'MANUAL' | 'DEFAULT'>>;
	handleManualCreateBaleScan: (action: 'ADD' | 'REMOVE', code: string) => Promise<void>;
	handleReceivingBaleScan: (action: 'ADD' | 'REMOVE', code: string) => Promise<void>;
	formData: ManualLoadCreateFormData;
	handleManualCreateInput: (
		key: keyof ManualLoadCreateFormData,
		value: string | number | string[] | CustomerValue | null
	) => void;
	handleManualCreateCustomerInput: (customer: CustomerValue | null) => void;
	scannedBales: ManualCreateScannedBale[];
	canSubmitManualCreate: boolean;
	setContamination: React.Dispatch<React.SetStateAction<number>>;
	contamination: number;
	setScannedBales: React.Dispatch<React.SetStateAction<ManualCreateScannedBale[]>>;
	handleCreateInboundLoad: () => Promise<void>;
	isSubmitting: boolean;
	// Add confirmation state
	isConfirmed: boolean;
	setIsConfirmed: React.Dispatch<React.SetStateAction<boolean>>;
	error: BaleInboundError;
	setError: React.Dispatch<React.SetStateAction<BaleInboundError>>;
	removeAllBales: () => void;
}

interface BaleInboundContextProviderProps {
	children: ReactNode;
}

/**
 * Represents a scanned bale with its material type and associated codes
 */
export interface ManualCreateScannedBale {
	name: string;
	value: MaterialType;
	emoji: string;
	count: number;
	codes: string[];
}

export type BaleInboundError = 'LOAD_NOT_FOUND' | null;

/**
 * Form data structure for the bale outbound workflow
 */
export interface ManualLoadCreateFormData {
	docketId: string | null;
	docketPhoto: string | null;
	originalDocketPhoto: string | null;
	loadPhotos: string[];
	weight: number | null;
	truckRegistration: string | null;
	expectedBales: ManualCreateScannedBale[];
	materialType: 'BULK_BALE' | 'BALE_LOOSE';
}

export interface CustomerValue {
	id: string;
	name: string;
}

// eslint-disable-next-line react-refresh/only-export-components
export const BaleInboundContext = createContext<BaleInboundContextValue | undefined>(undefined);

export const BaleInboundContextProvider: React.FC<BaleInboundContextProviderProps> = ({
	children,
}) => {
	const [error, setError] = useState<BaleInboundError>(null);
	const [searchTrackableAssets] = useSearchTrackableAssetsForFacilitiesBaleOutboundLazyQuery();
	const [createInboundLoad, { loading: isSubmitting }] = useCreateInboundLoadMutation();
	const [searchForLoad] = useSearchForLoadLazyQuery();
	const [uploadInboundBalePhotos] = useUploadInboundBalePhotoMutation();
	const [getUploadUrl] = useGetUploadUrlMutation();
	const { currentFacility } = useFacility();
	const { currentUser } = useCurrentUser();
	const [page, setPage] = useState<BaleInboundPageType>('INITIAL_SCAN');
	const [manualCodeEntryOpen, setManualCodeEntryOpen] = useState(false);
	const [workflowType, setWorkflowType] = useState<'MANUAL' | 'DEFAULT'>('DEFAULT');
	const [isConfirmed, setIsConfirmed] = useState(false);
	// Code tracking sets for preventing duplicates and tracking invalid codes
	const currentCodes = useSet();
	const invalidCodes = useSet();

	// Form data state
	const [formData, setFormData] = useState<ManualLoadCreateFormData>({
		docketId: null,
		docketPhoto: null,
		originalDocketPhoto: null,
		weight: null,
		truckRegistration: null,
		loadPhotos: [],
		expectedBales: [],
		materialType: 'BULK_BALE',
	});
	const [contamination, setContamination] = useState(25);
	const handleManualCreateInput = (
		key: keyof ManualLoadCreateFormData,
		value: string | number | string[] | CustomerValue | null
	) => {
		setFormData((prev) => ({ ...prev, [key]: value }));
	};
	const handleManualCreateCustomerInput = (customer: CustomerValue | null) => {
		setFormData((prev) => ({ ...prev, customer }));
	};

	// Initialize scanned bales with all material types at zero count
	const [scannedBales, setScannedBales] = useState<ManualCreateScannedBale[]>(
		Object.values(MaterialType)
			.map((materialType) => {
				const config = materialTypeFormat[materialType];
				return {
					name: config.label,
					value: materialType,
					emoji: config.emoji,
					count: 0,
					codes: [],
				};
			})
			.reverse()
	);

	const handleManualCreateBaleScan = async (action: 'ADD' | 'REMOVE', code: string) => {
		try {
			// Check for previously invalidated codes
			if (invalidCodes.has(code)) {
				// To do handle errors
				return;
			}

			// Handle ADD action validation
			if (action === 'ADD') {
				// Prevent duplicate scans in current session
				if (currentCodes.has(code)) {
					return;
				}
			}

			// Handle REMOVE action validation
			if (action === 'REMOVE') {
				// Check if code actually exists in scanned bales before API call
				const codeExists = scannedBales.some((bale) => bale.codes.includes(code));
				if (!codeExists) {
					// to do handle errors
					return;
				}
			}

			// Query API to validate and get bale information
			const data = await searchTrackableAssets({
				variables: { filter: { scannableCode: code.split('/').pop() } },
			}).then((res) => {
				if (res.data?.trackableAssets?.[0]) {
					return res.data.trackableAssets[0];
				}
				return null;
			});

			// Validate API response
			if (!data) {
				// TO DO HANDLE ERRORS
				return;
			}

			// Check bale status - only allow BALE_CREATED status
			if (data?.status && data.status !== 'BALE_CREATED') {
				// Handle additional errors
				return;
			}

			const materialType = data.intendedMaterial;

			// Update scanned bales state
			setScannedBales((prev) => {
				const newBales = [...prev];
				const index = newBales.findIndex((bale) => bale.value === materialType);

				if (index !== -1) {
					if (action === 'ADD') {
						// Add code if it doesn't already exist
						if (!newBales[index].codes.includes(code)) {
							newBales[index].codes.push(code);
							newBales[index].count += 1;
							currentCodes.add(code); // Track in current session
						}
					} else if (action === 'REMOVE') {
						// Remove code if it exists
						const codeIndex = newBales[index].codes.indexOf(code);
						if (codeIndex !== -1) {
							newBales[index].codes.splice(codeIndex, 1);
							newBales[index].count = Math.max(0, newBales[index].count - 1);
							currentCodes.delete(code); // Remove from current session tracking
						}
					}
				}
				return newBales;
			});
		} catch {
			// Handle any network errors or API errors
		} finally {
			// Handle errors here
		}
	};

	const handleReceivingBaleScan = async (action: 'ADD' | 'REMOVE', code: string) => {
		try {
			// Check for previously invalidated codes
			if (invalidCodes.has(code)) {
				// TO DO handle errors
				return;
			}

			// Handle ADD action validation
			if (action === 'ADD') {
				// Prevent duplicate scans in current session
				if (currentCodes.has(code)) {
					return;
				}
			}

			// Handle REMOVE action validation
			if (action === 'REMOVE') {
				// Check if code actually exists in scanned bales before API call
				const codeExists = scannedBales.some((bale) => bale.codes.includes(code));
				if (!codeExists) {
					// TO DO handle errors
					return;
				}
			}

			// Query API to validate and get bale information
			const data = await searchTrackableAssets({
				variables: { filter: { scannableCode: code.split('/').pop() } },
			}).then((res) => {
				if (res.data?.trackableAssets?.[0]) {
					return res.data.trackableAssets[0];
				}
				return null;
			});

			// Validate API response
			if (!data) {
				// TO DO HANDLE ERRORS
				return;
			}

			const materialType = data.intendedMaterial;

			// Update scanned bales state
			setScannedBales((prev) => {
				const newBales = [...prev];
				const index = newBales.findIndex((bale) => bale.value === materialType);

				if (index !== -1) {
					if (action === 'ADD') {
						// Add code if it doesn't already exist
						if (!newBales[index].codes.includes(code)) {
							newBales[index].codes.push(code);
							newBales[index].count += 1;
							currentCodes.add(code); // Track in current session
						}
					} else if (action === 'REMOVE') {
						// Remove code if it exists
						const codeIndex = newBales[index].codes.indexOf(code);
						if (codeIndex !== -1) {
							newBales[index].codes.splice(codeIndex, 1);
							newBales[index].count = Math.max(0, newBales[index].count - 1);
							currentCodes.delete(code); // Remove from current session tracking
						}
					}
				}

				return newBales;
			});
		} catch {
			// Handle any network errors or API errors
		} finally {
			// Handle errors here
		}
	};

	const canSubmitManualCreate = useMemo(() => {
		if (workflowType === 'MANUAL') {
			return Boolean(
				scannedBales.some((bale) => bale.count > 0) &&
					formData.docketId &&
					formData.weight &&
					formData.truckRegistration &&
					formData.docketPhoto
			);
		}
		return false;
	}, [
		workflowType,
		scannedBales,
		formData.docketId,
		formData.weight,
		formData.truckRegistration,
		formData.docketPhoto,
	]);

	const handleScan = async (
		code: string
	): Promise<{
		result: 'FOUND' | 'NOT_FOUND';
	}> => {
		// Reset confirmation state when starting new scan
		setIsConfirmed(false);

		// Placeholder logic
		const { data } = await searchForLoad({
			variables: {
				filter: {
					_or: [
						{
							docketId: code,
						},
						{
							containsTrackableAssets: {
								scannableCode: code.split('/').pop(),
							},
						},
					],
				},
			},
		});

		if (data?.trackableAssets?.[0]) {
			const rootAsset = data.trackableAssets[0];
			if (rootAsset.assetType === 'LOAD') {
				const docketPhoto = rootAsset.scanEvents?.flatMap((event) =>
					event.photos?.find((photo) => photo.photoType === 'DOCKET')
				)?.[0]?.photo?.url;
				const rootScanEvent = rootAsset.scanEvents?.find(
					(event) => event.scanEventType === ScanEventType.LoadDispatched
				);
				setFormData({
					docketId: rootAsset.docketId || null,
					docketPhoto: null,
					originalDocketPhoto: docketPhoto || null,
					weight: rootScanEvent?.weight || null,
					truckRegistration: rootAsset.truckRegistration || null,
					loadPhotos: [],
					materialType: 'BULK_BALE',
					expectedBales: Object.values(MaterialType)
						.map((materialType) => {
							const config = materialTypeFormat[materialType];
							const bales =
								rootAsset.containsTrackableAssets?.filter(
									(asset) => asset.intendedMaterial === materialType
								) || [];
							return {
								name: config.label,
								value: materialType,
								emoji: config.emoji,
								count: bales.length,
								codes: bales.map((bale) => bale.scannableCode || ''),
							};
						})
						.reverse(),
				});
				return {
					result: 'FOUND',
				};
			}
		}

		return {
			result: 'NOT_FOUND',
		};
	};

	const removeAllBales = () => {
		setScannedBales((prev) => prev.map((bale) => ({ ...bale, count: 0, codes: [] })));
		currentCodes.clear();
	};

	const handleCreateInboundLoad = useCallback(async () => {
		if (!currentFacility?.id) {
			throw new Error('No facility selected');
		}

		if (!formData.docketId) {
			throw new Error('Docket ID is required');
		}

		if (!formData.weight) {
			throw new Error('Load weight is required');
		}

		if (!formData.truckRegistration) {
			throw new Error('Truck registration is required');
		}

		// Get all scanned bale codes
		const allCodes = scannedBales.flatMap((bale) => bale.codes);

		try {
			// Step 1: Create the inbound load
			const result = await createInboundLoad({
				variables: {
					input: {
						facilityId: currentFacility.id,
						docketId: formData.docketId,
						weight: formData.weight,
						truckRegistration: formData.truckRegistration,
						codes: allCodes.length > 0 ? allCodes : undefined,
					},
				},
			});

			if (!result.data?.facilityBaleInbound?.id) {
				throw new Error('Failed to create inbound load');
			}

			const scanEventId = result.data.facilityBaleInbound.id;

			// Step 2: Upload photos if any exist
			const hasPhotos = formData.docketPhoto || formData.loadPhotos.length > 0;

			if (hasPhotos) {
				// Upload all photos to S3
				const photoUploads: { filename: string; type: ScanEventPhotoType }[] = [];

				/**
				 * Helper function to upload a single photo to S3
				 */
				async function uploadPhotoToS3(photo: string, type: ScanEventPhotoType) {
					// Generate unique S3 key
					const s3Key = `trackable-assets/load/${randomUUID()}.jpg`;

					// Get signed upload URL
					const { data: uploadUrlData } = await getUploadUrl({
						variables: { key: s3Key },
					});

					if (!uploadUrlData?.getUploadUrl) {
						throw new Error('Failed to get upload URL');
					}

					// Convert photo to bytes and upload
					const photoBytes = await fetch(photo).then((res) => res.arrayBuffer());
					const response = await fetch(uploadUrlData.getUploadUrl.url, {
						method: 'PUT',
						body: photoBytes,
						headers: {
							'Content-Type': 'image/jpeg',
						},
					});

					if (!response.ok) {
						throw new Error('Failed to upload photo');
					}

					return {
						filename: uploadUrlData.getUploadUrl.filename,
						type: type,
					};
				}

				// Upload all photos to S3 in parallel
				const uploadPromises = [
					// Upload all load photos
					...formData.loadPhotos.map((photo) =>
						uploadPhotoToS3(photo, 'LOAD' as ScanEventPhotoType)
					),
					// Upload docket photo if present
					...(formData.docketPhoto
						? [uploadPhotoToS3(formData.docketPhoto, 'DOCKET' as ScanEventPhotoType)]
						: []),
				];

				const uploadResults = await Promise.allSettled(uploadPromises);

				// Check for failed uploads
				const failedUploads = uploadResults.filter((result) => result.status === 'rejected');
				if (failedUploads.length > 0) {
					console.error('Some photos failed to upload to S3:', failedUploads);
					throw new Error('Failed to upload photos to S3');
				}

				// Collect successful uploads
				uploadResults.forEach((result) => {
					if (result.status === 'fulfilled') {
						photoUploads.push(result.value);
					}
				});

				// Step 3: Create database records for uploaded photos
				/**
				 * Helper function to create a database record for an uploaded photo
				 */
				async function createPhotoRecord(filename: string, type: ScanEventPhotoType) {
					const { data: photoData } = await uploadInboundBalePhotos({
						variables: {
							input: {
								scanEvent: {
									id: scanEventId,
								},
								photo: {
									filename,
									type: MediaType.Image,
								},
								createdAt: new Date().toISOString(),
								createdByApplicationType: 'mrf',
								createdByUser: {
									id: currentUser?.id ?? '',
								},
								photoType: type,
							},
						},
					});

					if (!photoData?.createScanEventPhoto) {
						throw new Error('Failed to create photo record');
					}

					return photoData.createScanEventPhoto;
				}

				// Create all database records in parallel
				const dbPromises = photoUploads.map(({ filename, type }) =>
					createPhotoRecord(filename, type)
				);

				const dbResults = await Promise.allSettled(dbPromises);

				// Check for failed database creations
				const failedDbCreations = dbResults.filter((result) => result.status === 'rejected');
				if (failedDbCreations.length > 0) {
					console.error('Some photo records failed to create:', failedDbCreations);
					// Note: Photos are already uploaded to S3, so this is a partial failure
				}
			}

			// Step 4: Successfully completed - reset to initial scan
			setPage('INITIAL_SCAN');
			// Reset form data
			setFormData({
				docketId: null,
				docketPhoto: null,
				originalDocketPhoto: null,
				weight: null,
				truckRegistration: null,
				loadPhotos: [],
				expectedBales: [],
				materialType: 'BULK_BALE',
			});
			// Reset scanned bales
			setScannedBales(
				Object.values(MaterialType)
					.map((materialType) => {
						const config = materialTypeFormat[materialType];
						return {
							name: config.label,
							value: materialType,
							emoji: config.emoji,
							count: 0,
							codes: [],
						};
					})
					.reverse()
			);
			// Clear current codes tracking
			currentCodes.clear();
			// Reset confirmation state
			setIsConfirmed(false);
		} catch (error) {
			// Handle specific errors from the backend
			console.error('Error creating inbound load:', error);
			throw error;
		}
	}, [
		currentFacility?.id,
		formData.docketId,
		formData.weight,
		formData.truckRegistration,
		formData.docketPhoto,
		formData.loadPhotos,
		scannedBales,
		createInboundLoad,
		getUploadUrl,
		uploadInboundBalePhotos,
		currentUser?.id,
		setPage,
		currentCodes,
		setIsConfirmed,
	]);

	return (
		<BaleInboundContext.Provider
			value={{
				page,
				setPage,
				manualCodeEntryOpen,
				setManualCodeEntryOpen,
				handleScan,
				workflowType,
				setWorkflowType,
				formData,
				handleManualCreateInput,
				handleManualCreateCustomerInput,
				handleManualCreateBaleScan,
				handleReceivingBaleScan,
				scannedBales,
				canSubmitManualCreate,
				contamination,
				setContamination,
				setScannedBales,
				handleCreateInboundLoad,
				isSubmitting,
				isConfirmed,
				setIsConfirmed,
				error,
				setError,
				removeAllBales,
			}}
		>
			{children}
		</BaleInboundContext.Provider>
	);
};
